/** Référence Nemo de l’éditeur de production pour les réseaux d’eau froide. */
import {
    Children,
    cloneElement,
    forwardRef,
    isValidElement,
    useCallback,
    useEffect,
    useMemo,
    useState,
    type ChangeEvent,
    type ChangeEventHandler,
    type CSSProperties,
    type Dispatch,
    type FocusEventHandler,
    type MouseEvent,
    type ReactElement,
    type ReactNode,
    type SetStateAction,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    NJButton,
    NJHeading,
    NJModal,
    NJModalContentInformation,
    NJText as FluidText,
    NJToastService,
    NJToastContainer,
    NJIconButton,
    NJToggle,
} from '@engie-group/fluid-design-system-react';
import { Position, type Edge, type Node, type NodeProps } from '@xyflow/react';
import { useForm } from 'react-hook-form';
import PrototypeStudioLayout from '@shared/PrototypeStudioLayout';
import {
    NetworkShell,
    Size2MixRightSideModal,
    Size2MixTabs,
    useSize2MixRefNavigation,
} from '@shared/size2mix-shell';
import {
    NODE_TYPES,
    CustomNode,
    ProductionEditorProvider,
    ProductionEditorCanvas,
    ProductionEditorOverlaySidebar,
    useProductionEditor,
    getNodeIcon,
    type ComponentItem,
    type AssetNode,
    type IBaseData,
} from '@shared/production-editor';
import type { NodeEditFormData, TabKey, GroupEditFormData, CondenserCoolingType } from './types';
import { useUpdateNode } from './hooks';
import { buildGraphForPlant, type PlantId, type GroupConfigs } from './graphs';
import { AnimatedFlowEdge } from './AnimatedEdge';
import { AnimatedFlowCyan } from './AnimatedFlowCyan';
import { AnimatedFlowGreen } from './AnimatedFlowGreen';
import { EntityFormPanel } from './EntityFormPanel';
import { EnergyContractsTab } from './EnergyContractsTab';
import { formatProductionLayout, type FlowNode } from './layout';
import centrifugalChillerIcon from './icons/centrifugal-chiller.png';
import departureIcon from './icons/departure.svg';
import iceStorageIcon from './icons/ice-storage.svg';
import multiCentrifugalChillerIcon from './icons/multi-centrifugal-chiller.png';
import returnIcon from './icons/return.svg';
import screwChillerIcon from './icons/screw-chiller.png';
import variableSpeedPumpIcon from './icons/variable-speed-pump.svg';
import waterStorageIcon from './icons/water-storage.svg';
import ViewerSwitcher from './ViewerSwitcher';
import { isViewerId, VIEWERS, type ViewerId } from './permissions';
import './flow-animation.css';

// ── Edge Types (edge animé pour flux) ──
const CUSTOM_EDGE_TYPES = {
    animatedFlow: AnimatedFlowEdge,
    animatedFlowCyan: AnimatedFlowCyan,
    animatedFlowGreen: AnimatedFlowGreen,
};

const ENTITY_HANDLES = [Position.Top, Position.Right, Position.Bottom, Position.Left];

function getColdEntityIconKey(data: Partial<IBaseData>): string | undefined {
    if (data.entityName === 'Return') return 'return';
    if (data.entityName === 'Departure') return 'departure';
    if (data.entityName === 'WaterCooling') return 'water-cooling';
    if (data.entityName === 'CoolingTower') return 'cooling-tower';
    if (data.entityName === 'VariableSpeedPump') return 'variable-speed-pump';
    if (data.entityName === 'WaterStorage' || data.storageType === 'water') return 'water-storage';
    if (data.entityName === 'StorageTank' || data.storageType === 'ice') return 'ice-storage';
    if (data.entityName !== 'Chiller') return undefined;

    const chillerType = String(data.chillerType ?? '').toLocaleLowerCase('fr');
    if (chillerType.includes('multi')) return 'multi-centrifugal-chiller';
    if (chillerType.includes('vis') || chillerType.includes('screw')) return 'screw-chiller';
    return 'centrifugal-chiller';
}

function ReferenceEntityNode({ id, selected, data }: NodeProps<AssetNode>) {
    const visualEntityName =
        data.entityName === 'WaterStorage' || data.entityName === 'StorageTank'
            ? 'Chiller'
            : data.entityName;

    return (
        <div
            data-cold-entity-icon={getColdEntityIconKey(data)}
            style={{ width: '100%', height: '100%' }}
        >
            <CustomNode
                id={id}
                selected={selected}
                entityName={visualEntityName}
                entityLabel={data.entityLabel}
                label={data.label}
                handles={ENTITY_HANDLES}
                onEditClick={data.onEditClick}
                onDeleteClick={data.onDeleteClick}
                chillerType={data.chillerType}
            />
        </div>
    );
}

const REFERENCE_NODE_TYPES = {
    ...NODE_TYPES,
    asset: ReferenceEntityNode,
    contract: ReferenceEntityNode,
};

// ── Meta ────────────────────────────────────────────────────────────────

export const meta = {
    title: 'Référence — Éditeur de production eau froide',
    description:
        'Référence Nemo de l’éditeur de production pour réseau d’eau froide, avec création, modification et profils d’accès homogénéisés.',
    tags: ['production', 'editor', 'reference'],
    author: 'stephane.coucke',
    status: 'review' as const,
};

const COLD_NETWORK = {
    name: 'tmp-e2e-01M5D29A3TBAZXQFCKGERX881R',
    fluidType: 'cold_water' as const,
};

const COLD_PRODUCTION_TABS = [
    { id: 'editor', label: 'Éditeur de production' },
    { id: 'contracts', label: "Contrats d'énergies" },
    { id: 'synthesis', label: 'Synthèse' },
] as const;

interface PersistedFlowNode {
    id: string;
    type?: string;
    position: { x: number; y: number };
    parentId?: string;
    width?: number;
    height?: number;
    data: IBaseData;
}

type NodeFormData = Partial<NodeEditFormData> & {
    entityLabel?: string;
    entityName?: string;
    label?: string;
    chillerType?: string;
    storageType?: 'water' | 'ice';
    isNew?: boolean;
};

type GroupFormNodeData = Partial<GroupEditFormData> & {
    entityLabel?: string;
    entityName?: string;
    isNew?: boolean;
    label?: string;
    electricityContract?: string;
    waterContract?: string;
};

type CompatTextVariant = 'heading-medium' | 'heading-small' | 'body-default' | 'body-small';

interface CompatTextProps {
    children?: ReactNode;
    variant?: CompatTextVariant;
    as?: 'div' | 'p' | 'span';
    scale?: string;
    style?: CSSProperties;
}

function NJText({ children, variant = 'body-default', as = 'p', scale, style }: CompatTextProps) {
    if (variant === 'heading-medium') {
        return (
            <NJHeading as={as === 'span' ? 'h4' : 'h3'} scale="md" style={style}>
                {children}
            </NJHeading>
        );
    }

    if (variant === 'heading-small') {
        return (
            <FluidText
                as={as}
                scale="md"
                style={{
                    color: 'var(--nj-semantic-color-text-neutral-primary-default)',
                    fontWeight: 'var(--nj-semantic-font-weight-bold)',
                    ...style,
                }}
            >
                {children}
            </FluidText>
        );
    }

    return (
        <FluidText as={as} scale={(scale ?? (variant === 'body-small' ? 'sm' : 'md')) as any} style={style}>
            {children}
        </FluidText>
    );
}

interface CompatFormItemProps {
    children?: ReactNode;
    label: string;
    required?: boolean;
    style?: CSSProperties;
}

function NJFormItem({ children, label, required = false, style }: CompatFormItemProps) {
    const displayedLabel = required && !label.trim().endsWith('*') ? `${label} *` : label;

    return (
        <div style={{ position: 'relative', ...style }}>
            <NJText
                as="span"
                variant="body-small"
                style={{
                    position: 'absolute',
                    top: '-8px',
                    left: 'var(--nj-semantic-size-spacing-8)',
                    zIndex: 1,
                    padding: '0 var(--nj-semantic-size-spacing-4)',
                    backgroundColor: 'var(--nj-semantic-color-background-neutral-primary-default)',
                    color: 'var(--nj-semantic-color-text-neutral-tertiary-default)',
                }}
            >
                {displayedLabel}
            </NJText>
            {children}
        </div>
    );
}

interface CompatInputProps {
    [key: string]: any;
    disabled?: boolean;
    isDisabled?: boolean;
    onBlur?: (...args: unknown[]) => unknown;
    onChange?: (...args: unknown[]) => unknown;
    style?: CSSProperties;
}

const NJInput = forwardRef<HTMLInputElement, CompatInputProps>(function NJInput(
    { isDisabled, disabled, onBlur, onChange, style, ...props },
    ref,
) {
    return (
        <input
            {...props}
            ref={ref}
            disabled={isDisabled ?? disabled}
            onBlur={onBlur as FocusEventHandler<HTMLInputElement>}
            onChange={onChange as ChangeEventHandler<HTMLInputElement>}
            style={{
                width: '100%',
                minHeight: 40,
                padding: '10px 12px',
                border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
                borderRadius: 'var(--nj-semantic-size-border-radius-none)',
                backgroundColor: 'var(--nj-semantic-color-background-neutral-primary-default)',
                color: 'var(--nj-semantic-color-text-neutral-primary-default)',
                ...style,
            }}
        />
    );
});

interface CompatSelectProps {
    [key: string]: any;
    onBlur?: (...args: unknown[]) => unknown;
    onChange?: (...args: unknown[]) => unknown;
    style?: CSSProperties;
}

const NJSelect = forwardRef<HTMLSelectElement, CompatSelectProps>(function NJSelect(
    { onBlur, onChange, style, ...props },
    ref,
) {
    return (
        <select
            {...props}
            ref={ref}
            onBlur={onBlur as FocusEventHandler<HTMLSelectElement>}
            onChange={onChange as ChangeEventHandler<HTMLSelectElement>}
            style={{
                width: '100%',
                minHeight: 40,
                padding: '10px 12px',
                border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
                borderRadius: 'var(--nj-semantic-size-border-radius-none)',
                backgroundColor: 'var(--nj-semantic-color-background-neutral-primary-default)',
                color: 'var(--nj-semantic-color-text-neutral-primary-default)',
                ...style,
            }}
        />
    );
});

interface CompatTabProps {
    ariaControls?: string;
    id?: string;
    isActive?: boolean;
    label: string;
    onClick?: (id: string, label: string, event: MouseEvent<HTMLButtonElement>) => void;
    value?: string;
}

function NJTab({ ariaControls, id, isActive = false, label, onClick, value }: CompatTabProps) {
    const tabId = value ?? id ?? label;

    return (
        <button
            aria-controls={ariaControls}
            aria-selected={isActive}
            onClick={(event) => onClick?.(tabId, label, event)}
            role="tab"
            style={{
                border: 'none',
                borderBottom: isActive
                    ? '2px solid var(--nj-semantic-color-border-brand-primary-default)'
                    : '2px solid transparent',
                background: 'transparent',
                padding: '12px 16px',
                cursor: 'pointer',
                color: isActive
                    ? 'var(--nj-semantic-color-text-brand-primary-default)'
                    : 'var(--nj-semantic-color-text-neutral-secondary-default)',
                fontWeight: isActive ? 600 : 400,
            }}
            type="button"
        >
            {label}
        </button>
    );
}

interface CompatTabsProps {
    children: ReactNode;
    label?: string;
    activeTab?: string;
    onClickTabItem?: (id: string, label: string, event: MouseEvent<HTMLButtonElement>) => void;
    onValueChange?: (value: string) => void;
    value?: string;
}

function NJTabs({ children, label, activeTab, onClickTabItem, onValueChange, value }: CompatTabsProps) {
    const currentTab = activeTab ?? value;
    const tabs = Children.toArray(children).filter((child): child is ReactElement<CompatTabProps> =>
        isValidElement<CompatTabProps>(child),
    );

    return (
        <div>
            <div aria-label={label} role="tablist" style={{ display: 'flex', gap: 4 }}>
                {tabs.map((child) => {
                    const tabId = child.props.value ?? child.props.id ?? child.props.label;
                    return cloneElement(child, {
                        ...child.props,
                        isActive: currentTab === tabId,
                        onClick: (id, tabLabel, event) => {
                            child.props.onClick?.(id, tabLabel, event);
                            onClickTabItem?.(id, tabLabel, event);
                            onValueChange?.(id);
                        },
                    });
                })}
            </div>
        </div>
    );
}

interface CompatTabPanelProps {
    children?: ReactNode;
    id: string;
    isActive?: boolean;
}

function NJTabPanel({ children, id, isActive = false }: CompatTabPanelProps) {
    if (!isActive) {
        return null;
    }

    return (
        <div id={id} role="tabpanel">
            {children}
        </div>
    );
}

// Centrales visibles : mapper IDs aux noms affichés
const VISIBLE_PLANTS = [
    { id: 'cber' as PlantId, label: 'AD-04' },
    { id: 'cauber' as PlantId, label: 'AD04 CH3&4' },
];

const COLD_COMPONENTS: ComponentItem[] = [
    { id: 'cold-return', label: 'Retour', entityName: 'Return', data: { componentGroup: 'Départ/Retour' } },
    { id: 'cold-departure', label: 'Départ', entityName: 'Departure', data: { componentGroup: 'Départ/Retour' } },
    { id: 'cold-chiller-centrifugal', label: 'Groupe froid centrifuge', entityName: 'Chiller', data: { componentGroup: 'Groupes froids', chillerType: 'Centrifuge' } },
    { id: 'cold-chiller-screw', label: 'Groupe froid à vis', entityName: 'Chiller', data: { componentGroup: 'Groupes froids', chillerType: 'À vis' } },
    { id: 'cold-chiller-multi', label: 'Groupe froid multi-centrifuge', entityName: 'Chiller', data: { componentGroup: 'Groupes froids', chillerType: 'Multi-centrifuge' } },
    { id: 'cold-water-cooling', label: 'Système de refroidissement à eau', entityName: 'WaterCooling', data: { componentGroup: 'Systèmes de refroidissement', coolingSystemType: 'water' } },
    { id: 'cold-air-cooling', label: 'Système de refroidissement à air', entityName: 'WaterCooling', data: { componentGroup: 'Systèmes de refroidissement', coolingSystemType: 'air' } },
    { id: 'cold-water-storage', label: 'Stockage eau', entityName: 'WaterStorage', data: { componentGroup: 'Stockages', storageType: 'water' } },
    { id: 'cold-storage-tank', label: 'Stockage glace', entityName: 'StorageTank', data: { componentGroup: 'Stockages', storageType: 'ice' } },
    { id: 'cold-pump', label: 'Pompe à vitesse variable', entityName: 'VariableSpeedPump', data: { componentGroup: 'Pompes' } },
].filter((item) => Boolean(getNodeIcon(item.entityName)));

function getColdEntityIcon(data: NodeFormData): string | undefined {
    if (data.entityName === 'Return') return returnIcon;
    if (data.entityName === 'Departure') return departureIcon;
    if (data.entityName === 'Chiller') {
        const chillerType = data.chillerType?.toLocaleLowerCase('fr') ?? '';
        if (chillerType.includes('multi')) return multiCentrifugalChillerIcon;
        if (chillerType.includes('vis') || chillerType.includes('screw')) return screwChillerIcon;
        return centrifugalChillerIcon;
    }
    if (data.entityName === 'VariableSpeedPump') return variableSpeedPumpIcon;
    if (data.storageType === 'water' || data.entityName === 'WaterStorage') return waterStorageIcon;
    if (data.storageType === 'ice' || data.entityName === 'StorageTank') return iceStorageIcon;
    return getNodeIcon(data.entityName ?? '');
}

function persistNodes(plantId: PlantId, nodesToPersist: FlowNode[]) {
    const persistedNodes: PersistedFlowNode[] = nodesToPersist.map((node) => {
        const { onEditClick, onDeleteClick, ...data } = node.data;
        return {
            id: node.id,
            type: node.type,
            position: node.position,
            parentId: node.parentId,
            width: node.measured?.width ?? node.width,
            height: node.measured?.height ?? node.height,
            data,
        };
    });
    localStorage.setItem(`cooling-system-2-${plantId}`, JSON.stringify(persistedNodes));
}

// ── Canvas Toolbar Component ────────────────────────────────────────────

function CanvasToolbar({
    autoSaveEnabled,
    onAutoSaveChange,
    onSave,
}: {
    autoSaveEnabled: boolean;
    onAutoSaveChange: (enabled: boolean) => void;
    onSave: () => void;
}) {
    const toggleFullscreen = (event: MouseEvent<HTMLButtonElement>) => {
        if (document.fullscreenElement) {
            void document.exitFullscreen();
            return;
        }
        const canvas = event.currentTarget.closest('.react-flow');
        if (canvas instanceof HTMLElement) void canvas.requestFullscreen();
    };

    return (
        <div style={{ position: 'absolute', top: 12, right: 16, display: 'flex', gap: 8, zIndex: 10 }}>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    paddingLeft: 'var(--nj-semantic-size-spacing-8)',
                    border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
                    borderRadius: 'var(--nj-semantic-size-border-radius-none)',
                    background: 'var(--nj-semantic-color-background-neutral-primary-default)',
                }}
            >
                <NJToggle
                    aria-label="Sauvegarde automatique"
                    checked={autoSaveEnabled}
                    onChange={(_, checked) => onAutoSaveChange(checked)}
                />
                <NJIconButton label="Enregistrer" icon="save" variant="tertiary" scale="sm" onClick={onSave} />
            </div>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
                    borderRadius: 'var(--nj-semantic-size-border-radius-none)',
                    background: 'var(--nj-semantic-color-background-neutral-primary-default)',
                }}
            >
                <NJIconButton
                    label="Plein écran"
                    icon="fullscreen"
                    variant="tertiary"
                    scale="sm"
                    onClick={toggleFullscreen}
                />
            </div>
        </div>
    );
}

// ── Canvas Component ────────────────────────────────────────────────────

interface CoolingSystem2CanvasProps {
    selectedPlantId: PlantId;
    onPlantChange: (plantId: PlantId) => void;
    groupConfigs: GroupConfigs;
    onGroupConfigChange: (groupId: string, config: CondenserCoolingType) => void;
    viewerId: ViewerId;
    canWrite: boolean;
}

function CoolingSystem2Canvas({
    selectedPlantId,
    onPlantChange,
    groupConfigs,
    onGroupConfigChange,
    viewerId,
    canWrite,
}: CoolingSystem2CanvasProps) {
    const { nodes: rawNodes, setNodes: setRawNodes } = useProductionEditor();
    const nodes = rawNodes as FlowNode[];
    const setNodes = setRawNodes as Dispatch<SetStateAction<FlowNode[]>>;
    const [editingNodeId, setEditingNodeId] = useState<string | null>(null);
    const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
    const [deletingNodeId, setDeletingNodeId] = useState<string | null>(null);
    const [condenserType, setCondenserType] = useState<CondenserCoolingType>('parallel');
    const [activeCharacteristicTab, setActiveCharacteristicTab] = useState<string>('pressure');
    const [showAdvancedOptions, setShowAdvancedOptions] = useState<boolean>(false);
    const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
    
    // Historique pour undo/redo
    const [history, setHistory] = useState<FlowNode[][]>([]);
    const [historyIndex, setHistoryIndex] = useState<number>(-1);
    
    const updateNodeMutation = useUpdateNode(viewerId);

    useEffect(() => {
        const frameId = window.requestAnimationFrame(() => {
            document
                .querySelectorAll<HTMLElement>('.react-flow [aria-label^="Glisser "]')
                .forEach((tile) => {
                    const label = tile.getAttribute('aria-label')?.replace(/^Glisser\s+/, '');
                    if (label) tile.title = label;
                });
        });
        return () => window.cancelAnimationFrame(frameId);
    }, [selectedPlantId]);

    // Initialiser l'historique avec l'état initial
    useEffect(() => {
        if (nodes.length > 0 && history.length === 0) {
            setHistory([nodes]);
            setHistoryIndex(0);
        }
    }, [nodes, history.length]);

    // Sauvegarder les positions ET les data des nodes dans localStorage (par centrale)
    const saveNodesPosition = useCallback(() => {
        persistNodes(selectedPlantId, nodes);
        NJToastService('Positions sauvegardées', {
            iconName: 'check_circle',
            dismissAfter: 2000,
        });
    }, [nodes, selectedPlantId]);

    // Auto-save toutes les 30 secondes
    useEffect(() => {
        if (autoSaveEnabled) {
            const interval = setInterval(() => {
                persistNodes(selectedPlantId, nodes);
            }, 30000);
            return () => clearInterval(interval);
        }
    }, [autoSaveEnabled, nodes, selectedPlantId]);

    // Sauvegarde automatique après modification des nodes (debounced)
    useEffect(() => {
        if (!autoSaveEnabled) return undefined;
        const timeoutId = setTimeout(() => {
            persistNodes(selectedPlantId, nodes);
        }, 500); // Debounce de 500ms

        return () => clearTimeout(timeoutId);
    }, [nodes, selectedPlantId]);

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isDirty, isValid },
    } = useForm<NodeEditFormData>({ mode: 'onChange' });
    const {
        register: registerGroup,
        handleSubmit: handleSubmitGroup,
        setValue: setValueGroup,
        formState: { isDirty: isGroupDirty, isValid: isGroupValid },
    } = useForm<GroupEditFormData>({ mode: 'onChange' });

    // Trouver le node en édition/suppression
    const editingNode = nodes.find((n) => n.id === editingNodeId);
    const editingGroup = nodes.find((n) => n.id === editingGroupId);
    const deletingNode = nodes.find((n) => n.id === deletingNodeId);
    const editingNodeData = (editingNode?.data ?? {}) as NodeFormData;
    const editingGroupData = (editingGroup?.data ?? {}) as GroupFormNodeData;
    const deletingNodeData = (deletingNode?.data ?? {}) as Partial<IBaseData>;

    const closeNodeEditor = useCallback(() => {
        if (editingNodeData.isNew && editingNodeId) {
            setNodes((currentNodes) => currentNodes.filter((node) => node.id !== editingNodeId));
        }
        setEditingNodeId(null);
    }, [editingNodeData.isNew, editingNodeId, setNodes]);

    const closeGroupEditor = useCallback(() => {
        if (editingGroupData.isNew && editingGroupId) {
            setNodes((currentNodes) => currentNodes.filter((node) => node.id !== editingGroupId));
        }
        setEditingGroupId(null);
    }, [editingGroupData.isNew, editingGroupId, setNodes]);

    // Sauvegarder dans l'historique pour undo/redo
    const saveToHistory = useCallback((newNodes: FlowNode[]) => {
        setHistory((prev) => {
            // Limiter à 20 états dans l'historique
            const newHistory = [...prev.slice(0, historyIndex + 1), newNodes].slice(-20);
            return newHistory;
        });
        setHistoryIndex((prev) => Math.min(prev + 1, 19));
    }, [historyIndex]);

    // Callbacks pour les actions edit/delete/duplicate - versions stables qui utilisent les états
    const handleNodeEdit = useCallback((nodeId: string) => {
        const node = nodes.find((n) => n.id === nodeId);
        // Vérifier si c'est un groupe (type='group')
        if (node?.type === 'group') {
            // C'est un groupe, ouvrir le formulaire de groupe
            setEditingGroupId(nodeId);
        } else {
            // C'est un node normal, ouvrir le formulaire de node
            setEditingNodeId(nodeId);
        }
    }, [nodes]);

    const handleNodeDelete = useCallback((nodeId: string) => {
        setDeletingNodeId(nodeId);
    }, []);

    // Fonction pour ajuster automatiquement la taille et centrer le contenu d'un groupe
    const adjustGroupSize = useCallback((_groupId: string) => {
        setNodes((prevNodes) => formatProductionLayout(prevNodes));
    }, [setNodes]);

    // Undo (Ctrl+Z)
    const handleUndo = useCallback(() => {
        if (historyIndex > 0) {
            const previousState = history[historyIndex - 1];
            setNodes(previousState);
            setHistoryIndex(historyIndex - 1);
            NJToastService('Action annulée', {
                iconName: 'undo',
                dismissAfter: 1500,
            });
        }
    }, [history, historyIndex, setNodes]);

    // Redo (Ctrl+Y)
    const handleRedo = useCallback(() => {
        if (historyIndex < history.length - 1) {
            const nextState = history[historyIndex + 1];
            setNodes(nextState);
            setHistoryIndex(historyIndex + 1);
            NJToastService('Action rétablie', {
                iconName: 'redo',
                dismissAfter: 1500,
            });
        }
    }, [history, historyIndex, setNodes]);

    // Raccourcis clavier
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (!canWrite) return;
            // Ignorer si on est dans un input/textarea
            if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
                return;
            }

            // Ctrl+Z : Undo
            if (event.ctrlKey && event.key === 'z' && !event.shiftKey) {
                event.preventDefault();
                handleUndo();
            }

            // Ctrl+Y ou Ctrl+Shift+Z : Redo
            if ((event.ctrlKey && event.key === 'y') || (event.ctrlKey && event.shiftKey && event.key === 'z')) {
                event.preventDefault();
                handleRedo();
            }

            // Suppr (Delete) : Supprimer l'entité sélectionnée
            if (event.key === 'Delete') {
                const selectedNode = nodes.find(n => n.selected);
                if (selectedNode) {
                    event.preventDefault();
                    handleNodeDelete(selectedNode.id);
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [canWrite, handleUndo, handleRedo, nodes, handleNodeDelete]);

    // Réinitialiser le formulaire quand un node est sélectionné pour édition
    useEffect(() => {
        if (editingNodeId && editingNode) {
            const nodeData = editingNode.data;
            
            // Initialiser les champs communs
            reset({ 
                identifier: String((nodeData as NodeFormData).identifier ?? editingNode.id),
                label: nodeData.label,
                // TAR-specific fields
                ...(nodeData.entityName === 'CoolingTower' && {
                    nominalElectricalPower: (nodeData as any).nominalElectricalPower,
                    nominalThermalPower: (nodeData as any).nominalThermalPower,
                    numberOfFans: (nodeData as any).numberOfFans,
                    electricPowerAlpha: (nodeData as any).electricPowerAlpha,
                    electricPowerBeta: (nodeData as any).electricPowerBeta,
                    fansElectricPowerGamma: (nodeData as any).fansElectricPowerGamma,
                    fanElectricPowerCoeffM: (nodeData as any).fanElectricPowerCoeffM,
                    airCoolingSystem: (nodeData as any).airCoolingSystem,
                }),
                // Pump-specific fields
                ...(nodeData.entityName === 'VariableSpeedPump' && {
                    nominalRotationSpeed: (nodeData as any).nominalRotationSpeed,
                    minRotationSpeed: (nodeData as any).minRotationSpeed,
                    flowRate: (nodeData as any).flowRate,
                    pressure: (nodeData as any).pressure,
                }),
                // Chiller-specific fields
                ...(nodeData.entityName === 'Chiller' && {
                    electricityContract: (nodeData as any).electricityContract,
                    compressorCapacity: (nodeData as any).compressorCapacity,
                    linkedExchanger: (nodeData as any).linkedExchanger,
                    nominalCoolingCapacity: (nodeData as any).nominalCoolingCapacity,
                    partialLoadMinRatio: (nodeData as any).partialLoadMinRatio,
                    partialLoadMaxRatio: (nodeData as any).partialLoadMaxRatio,
                    nominalEER: (nodeData as any).nominalEER,
                    evaporatorMinInletTemp: (nodeData as any).evaporatorMinInletTemp,
                    evaporatorMaxInletTemp: (nodeData as any).evaporatorMaxInletTemp,
                    condenserMinInletTemp: (nodeData as any).condenserMinInletTemp,
                    condenserMaxInletTemp: (nodeData as any).condenserMaxInletTemp,
                }),
            });
        }
    }, [editingNodeId, reset]);

    useEffect(() => {
        if (editingNodeId) setShowAdvancedOptions(false);
    }, [editingNodeId]);

    // Réinitialiser le formulaire de groupe quand un groupe est sélectionné
    useEffect(() => {
        if (editingGroupId && editingGroup) {
            const isPumpGroup = editingGroup.data.entityLabel === 'Groupe de pompes';
            const isCoolingSystem = editingGroup.data.entityName === 'WaterCooling';
            
            if (isPumpGroup) {
                // Pour les groupes de pompes, initialiser le formulaire avec label et electricityContract
                setTimeout(() => {
                    setValueGroup('identifier', String(editingGroupData.identifier ?? editingGroup.id));
                    setValueGroup('label', editingGroupData.label ?? '');
                    setValueGroup('electricityContract', editingGroupData.electricityContract ?? '');
                }, 0);
            } else if (isCoolingSystem) {
                // Pour les systèmes de refroidissement, initialiser avec tous les champs techniques
                setTimeout(() => {
                    setValueGroup('identifier', String(editingGroupData.identifier ?? editingGroup.id));
                    setValueGroup('label', editingGroupData.label ?? '');
                    setValueGroup('linkedProductionLines', editingGroupData.linkedProductionLines ?? []);
                    setValueGroup('condenserInletTempMin', editingGroupData.condenserInletTempMin ?? 288.15);
                    setValueGroup('condenserOutletTempMax', editingGroupData.condenserOutletTempMax ?? 303.15);
                    setValueGroup('electricityContract', editingGroupData.electricityContract ?? '');
                    setValueGroup('waterContract', editingGroupData.waterContract ?? '');
                    setValueGroup('linkedCoolingTowers', editingGroupData.linkedCoolingTowers ?? []);
                    setValueGroup('condenserInletAlpha', editingGroupData.condenserInletAlpha ?? -9.3925);
                    setValueGroup('condenserInletBeta', editingGroupData.condenserInletBeta ?? 1528.6);
                    setValueGroup('condenserInletGamma', editingGroupData.condenserInletGamma ?? 0.2161);
                    setValueGroup('condenserInletEta', editingGroupData.condenserInletEta ?? 0.0169);
                    setValueGroup('waterFlowAlpha', editingGroupData.waterFlowAlpha ?? 4.0272);
                    setValueGroup('waterFlowBeta', editingGroupData.waterFlowBeta ?? 0);
                    setValueGroup('softenerEfficiency', editingGroupData.softenerEfficiency ?? 0.05);
                    setValueGroup('tarStaticHeight', editingGroupData.tarStaticHeight ?? 0);
                    setValueGroup('tarPressureLoss', editingGroupData.tarPressureLoss ?? 0);
                    setValueGroup('tarNominalFlow', editingGroupData.tarNominalFlow ?? 0);
                }, 0);
            } else {
                // Pour les lignes de production, initialiser avec la config des condenseurs
                const currentConfig = groupConfigs[editingGroupId] || 'parallel';
                setCondenserType(currentConfig);
                setTimeout(() => {
                    setValueGroup('identifier', String(editingGroupData.identifier ?? editingGroup.id));
                    setValueGroup('label', editingGroupData.label ?? '');
                }, 0);
            }
        }
    }, [editingGroupId, groupConfigs, setValueGroup]);

    // Injecter les callbacks dans les nodes avec capture de l'ID
    useEffect(() => {
        setNodes((prevNodes) =>
            prevNodes.map((node) => ({
                ...node,
                data: {
                    ...node.data,
                    onEditClick: canWrite ? () => handleNodeEdit(node.id) : undefined,
                    onDeleteClick: canWrite ? () => handleNodeDelete(node.id) : undefined,
                },
            }))
        );
    }, [canWrite, handleNodeEdit, handleNodeDelete, setNodes, selectedPlantId]);

    useEffect(() => {
        if (!canWrite || editingNodeId || editingGroupId) return;
        const createdNode = nodes.find((node) => (node.data as NodeFormData).isNew);
        if (!createdNode) return;
        if (createdNode.type === 'group') {
            setEditingGroupId(createdNode.id);
        } else {
            setEditingNodeId(createdNode.id);
        }
    }, [canWrite, editingGroupId, editingNodeId, nodes]);

    // Sauvegarder les modifications
    const onSubmitEdit = useCallback(
        (data: NodeEditFormData) => {
            if (!editingNodeId) return;
            const wasCreation = Boolean(editingNodeData.isNew);

            updateNodeMutation.mutate(
                { nodeId: editingNodeId, data: data as unknown as Record<string, unknown> },
                {
                    onSuccess: () => {
                        setNodes((prevNodes) => {
                            const updatedNodes = prevNodes.map((node) =>
                                node.id === editingNodeId
                                    ? {
                                        ...node,
                                        data: {
                                            ...node.data,
                                            ...data,
                                            isNew: false,
                                        },
                                    }
                                    : node,
                            );
                            persistNodes(selectedPlantId, updatedNodes);
                            return updatedNodes;
                        });
                        setEditingNodeId(null);
                        NJToastService(
                            wasCreation
                                ? `${editingNodeData.entityLabel ?? 'Entité'} créée avec succès`
                                : `${editingNodeData.entityLabel ?? 'Entité'} mise à jour avec succès`,
                            {
                            iconName: 'check_circle',
                            dismissAfter: 3000,
                            },
                        );
                    },
                    onError: (error) => {
                        NJToastService(error instanceof Error ? error.message : 'La configuration n’a pas pu être enregistrée', {
                            iconName: 'error',
                            dismissAfter: 4000,
                        });
                    },
                },
            );
        },
        [editingNodeData.entityLabel, editingNodeData.isNew, editingNodeId, selectedPlantId, setNodes, updateNodeMutation],
    );

    // Sauvegarder les modifications de groupe
    const onSubmitGroup = useCallback(
        (data: GroupEditFormData) => {
            if (!editingGroupId || !canWrite) return;
            const wasCreation = Boolean(editingGroupData.isNew);
            
            const isPumpGroup = editingGroupData.entityLabel === 'Groupe de pompes';
            const isCoolingSystem = editingGroupData.entityName === 'WaterCooling';
            
            // Mettre à jour la configuration du groupe avec l'état condenserType (sauf pour groupe de pompes et système de refroidissement)
            if (!isPumpGroup && !isCoolingSystem) {
                onGroupConfigChange(editingGroupId, condenserType);
            }
            
            // Mettre à jour le label et les autres champs du node
            if (data.label) {
                setNodes((prevNodes) => {
                    const updatedNodes = prevNodes.map((node) =>
                        node.id === editingGroupId
                            ? { 
                                ...node, 
                                data: { 
                                    ...node.data, 
                                    identifier: data.identifier,
                                    label: data.label,
                                    isNew: false,
                                    ...(isPumpGroup 
                                        ? { electricityContract: data.electricityContract || null }
                                        : isCoolingSystem
                                        ? {
                                            linkedProductionLines: data.linkedProductionLines,
                                            condenserInletTempMin: data.condenserInletTempMin,
                                            condenserOutletTempMax: data.condenserOutletTempMax,
                                            electricityContract: data.electricityContract,
                                            waterContract: data.waterContract,
                                            linkedCoolingTowers: data.linkedCoolingTowers,
                                            condenserInletAlpha: data.condenserInletAlpha,
                                            condenserInletBeta: data.condenserInletBeta,
                                            condenserInletGamma: data.condenserInletGamma,
                                            condenserInletEta: data.condenserInletEta,
                                            waterFlowAlpha: data.waterFlowAlpha,
                                            waterFlowBeta: data.waterFlowBeta,
                                            softenerEfficiency: data.softenerEfficiency,
                                            tarStaticHeight: data.tarStaticHeight,
                                            tarPressureLoss: data.tarPressureLoss,
                                            tarNominalFlow: data.tarNominalFlow,
                                        }
                                        : { condenserCoolingType: condenserType }
                                    )
                                } 
                            }
                            : node
                    );
                    persistNodes(selectedPlantId, updatedNodes);
                    return updatedNodes;
                });
            }
            
            // Sauvegarder les nodes après modification (sera fait via useEffect)
            
            setEditingGroupId(null);
            NJToastService(wasCreation ? 'Entité créée avec succès' : 'Configuration du groupe mise à jour', {
                iconName: 'check_circle',
                dismissAfter: 3000,
            });
        },
        [canWrite, condenserType, editingGroupData.entityLabel, editingGroupData.isNew, editingGroupId, onGroupConfigChange, selectedPlantId, setNodes]
    );

    // Supprimer un nœud
    const handleDeleteNode = useCallback(() => {
        if (!deletingNodeId || !canWrite) return;
        const nodeToDelete = nodes.find((n) => n.id === deletingNodeId);
        if (!nodeToDelete) return;

        const nodeLabel = nodeToDelete.data.label || 'Élément';
        const isGroup = nodeToDelete.type === 'group';
        const parentGroupId = nodeToDelete.parentId; // Capturer le parentId avant suppression

        // Si c'est un groupe, compter ses enfants
        const childrenNodes = isGroup ? nodes.filter((n) => n.parentId === deletingNodeId) : [];

        // Sauvegarder l'état actuel dans l'historique avant suppression
        saveToHistory(nodes);

        // Supprimer le node et ses enfants du canvas
        setNodes((prevNodes) => {
            // Filtrer pour retirer le node et ses enfants
            return prevNodes.filter((n) => 
                n.id !== deletingNodeId && n.parentId !== deletingNodeId
            );
        });

        // Fermer la modale
        setDeletingNodeId(null);

        // Afficher le toast de confirmation
        if (isGroup && childrenNodes.length > 0) {
            NJToastService(`Groupe "${nodeLabel}" et ${childrenNodes.length} enfant(s) supprimés`, {
                iconName: 'check_circle',
                dismissAfter: 3000,
            });
        } else {
            NJToastService(`"${nodeLabel}" supprimé`, {
                iconName: 'check_circle',
                dismissAfter: 2000,
            });
        }
        
        // Si l'élément supprimé appartenait à un groupe, ajuster la taille du groupe parent
        if (parentGroupId && !isGroup) {
            setTimeout(() => adjustGroupSize(parentGroupId), 100);
        }
    }, [adjustGroupSize, canWrite, deletingNodeId, nodes, saveToHistory, setNodes]);

    return (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
            <ProductionEditorCanvas
                nodeTypes={REFERENCE_NODE_TYPES}
                edgeTypes={CUSTOM_EDGE_TYPES}
                minHeight="100%"
                nodeFactory={({ id, position, draggedType, label, data }) => ({
                    id,
                    type:
                        draggedType === 'WaterCooling'
                            ? 'group'
                            : draggedType.endsWith('Contract')
                              ? 'contract'
                              : 'asset',
                    position,
                    data: {
                        entityName: draggedType,
                        entityLabel: label,
                        label: `Nouveau ${label}`,
                        identifier: id,
                        isNew: true,
                        ...data,
                    },
                })}
            >
                <style>
                    {`
                        [aria-label="Glisser Retour"] img,
                        [data-cold-entity-icon="return"] img {
                            content: url("${returnIcon}");
                        }
                        [aria-label="Glisser Départ"] img,
                        [data-cold-entity-icon="departure"] img {
                            content: url("${departureIcon}");
                        }
                        [aria-label="Glisser Stockage eau"] img,
                        [data-cold-entity-icon="water-storage"] img {
                            content: url("${waterStorageIcon}");
                        }
                        [aria-label="Glisser Stockage glace"] img,
                        [data-cold-entity-icon="ice-storage"] img {
                            content: url("${iceStorageIcon}");
                        }
                        [aria-label="Glisser Groupe froid centrifuge"] img,
                        [data-cold-entity-icon="centrifugal-chiller"] img {
                            content: url("${centrifugalChillerIcon}");
                        }
                        [aria-label="Glisser Groupe froid à vis"] img,
                        [data-cold-entity-icon="screw-chiller"] img {
                            content: url("${screwChillerIcon}");
                        }
                        [aria-label="Glisser Groupe froid multi-centrifuge"] img,
                        [data-cold-entity-icon="multi-centrifugal-chiller"] img {
                            content: url("${multiCentrifugalChillerIcon}");
                        }
                        [data-cold-entity-icon$="chiller"] img {
                            object-fit: contain;
                        }
                        [aria-label="Glisser Pompe à vitesse variable"] img,
                        [data-cold-entity-icon="variable-speed-pump"] img {
                            content: url("${variableSpeedPumpIcon}");
                        }
                        [aria-label^="Glisser"] img {
                            width: 48px;
                            height: 48px;
                            object-fit: contain;
                        }
                    `}
                </style>
                <ProductionEditorOverlaySidebar
                    plants={VISIBLE_PLANTS}
                    components={COLD_COMPONENTS}
                    selectedPlantId={selectedPlantId}
                    onSelectPlant={(plantId) => onPlantChange(plantId as PlantId)}
                    onCreatePlant={
                        canWrite
                            ? () =>
                                NJToastService(
                                    'La création de centrale nécessite l’extension mainteneur du kit pour le panneau Stockages réseau.',
                                    { iconName: 'info', dismissAfter: 4500 },
                                )
                            : undefined
                    }
                    showComponents={canWrite}
                    plantsLabel="Centrales"
                    componentsLabel="Composants"
                    createPlantLabel="Créer une centrale"
                    width={300}
                />
                {canWrite && (
                    <CanvasToolbar
                        autoSaveEnabled={autoSaveEnabled}
                        onAutoSaveChange={setAutoSaveEnabled}
                        onSave={saveNodesPosition}
                    />
                )}
            </ProductionEditorCanvas>

            {/* Modale d'édition enrichie (overlay droite) */}
            {editingNodeId && (
                <EntityFormPanel
                    iconSrc={getColdEntityIcon(editingNodeData)}
                    isCreation={Boolean(editingNodeData.isNew)}
                    isDirty={isDirty}
                    isPending={updateNodeMutation.isPending}
                    isValid={isValid}
                    metadata={
                        editingNodeData.isNew
                            ? 'Nouvelle entité — les données ne sont conservées qu’après validation.'
                            : 'Créé par nemo - e2e-analyst-1 le 23-10-2026 à 10:50:48, dernière modification 23-10-2026 à 10:50:48'
                    }
                    onClose={closeNodeEditor}
                    onSubmit={handleSubmit(onSubmitEdit)}
                    title={String(editingNodeData.label ?? editingNodeData.entityLabel ?? '')}
                >
                            {/* Identifiant */}
                            <NJFormItem label="Identifiant *" required>
                                <NJInput
                                    {...register('identifier', {
                                        required: 'L’identifiant est obligatoire',
                                        maxLength: { value: 64, message: '64 caractères maximum' },
                                        pattern: {
                                            value: /^[A-Za-z0-9][A-Za-z0-9_-]*$/,
                                            message: 'Utilisez uniquement lettres, chiffres, tirets et underscores',
                                        },
                                    })}
                                    aria-invalid={Boolean(errors.identifier)}
                                    placeholder="identifiant-technique"
                                />
                                {errors.identifier?.message && (
                                    <NJText scale="xs" style={{ color: 'var(--nj-semantic-color-text-status-danger-default)', marginTop: 4 }}>
                                        {errors.identifier.message}
                                    </NJText>
                                )}
                            </NJFormItem>
                            
                            {/* Nom */}
                            <NJFormItem label="Nom *" required style={{ marginTop: '16px' }}>
                                <NJInput 
                                    {...register('label', {
                                        required: 'Le nom est obligatoire',
                                        maxLength: { value: 64, message: '64 caractères maximum' },
                                    })}
                                    aria-invalid={Boolean(errors.label)}
                                    placeholder="Nom de l'entité"
                                />
                                {errors.label?.message && (
                                    <NJText scale="xs" style={{ color: 'var(--nj-semantic-color-text-status-danger-default)', marginTop: 4 }}>
                                        {errors.label.message}
                                    </NJText>
                                )}
                            </NJFormItem>

                            {editingNodeData.entityName === 'Return' ? (
                                <>
                                    <div style={{ marginTop: '20px' }}>
                                        <NJInput aria-label="Altitude (m)" disabled placeholder="Altitude (m)" />
                                    </div>
                                    <div style={{ marginTop: '20px' }}>
                                        <NJSelect aria-label="Retour" disabled value="">
                                            <option value="">Retour</option>
                                        </NJSelect>
                                    </div>
                                </>
                            ) : (
                                <NJFormItem label="Type" style={{ marginTop: '16px' }}>
                                    <NJInput value={editingNodeData.entityLabel} disabled style={{ backgroundColor: 'var(--nj-semantic-color-surface-disabled)' }} />
                                </NJFormItem>
                            )}

                            {/* Champs spécifiques Pompe */}
                            {editingNodeData.entityName === 'VariableSpeedPump' && (
                                <>
                                    <div style={{ marginTop: '32px', marginBottom: '16px' }}>
                                        <NJText variant="heading-small">Courbe Caractéristique</NJText>
                                    </div>
                                    
                                    <NJTabs 
                                        label="Courbes"
                                        activeTab={activeCharacteristicTab} 
                                        onClickTabItem={(id) => setActiveCharacteristicTab(id as string)}
                                    >
                                        <NJTab id="pressure" label="Data de pression" ariaControls="panel-pressure" />
                                        <NJTab id="performance" label="Rendement" ariaControls="panel-performance" />
                                    </NJTabs>
                                    
                                    <NJTabPanel id="panel-pressure" isActive={activeCharacteristicTab === 'pressure'}>
                                        <div style={{ marginTop: '16px', padding: '16px', backgroundColor: 'var(--nj-semantic-color-surface-subtle)', borderRadius: '8px' }}>
                                            <div style={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed var(--nj-core-color-grey-300)', borderRadius: '4px' }}>
                                                <NJText variant="body-default" style={{ color: 'var(--nj-semantic-color-text-neutral-tertiary-default)' }}>
                                                    📊 Courbe de pression
                                                </NJText>
                                            </div>
                                        </div>
                                    </NJTabPanel>
                                    
                                    <NJTabPanel id="panel-performance" isActive={activeCharacteristicTab === 'performance'}>
                                        <div style={{ marginTop: '16px', padding: '16px' }}>
                                            <NJText>Données de rendement...</NJText>
                                        </div>
                                    </NJTabPanel>

                                    <div style={{ marginTop: '32px', marginBottom: '16px' }}>
                                        <NJText variant="heading-small">Paramètres</NJText>
                                    </div>

                                    <NJFormItem label="Vitesse de rotation nominale (rad/s)" style={{ marginTop: '16px' }}>
                                        <NJInput type="number" step="0.00001" {...register('nominalRotationSpeed')} defaultValue="152.99551" />
                                    </NJFormItem>

                                    <NJFormItem label="Vitesse de rotation minimale (rad/s)" style={{ marginTop: '16px' }}>
                                        <NJInput type="number" step="0.0001" {...register('minRotationSpeed')} defaultValue="1.0472" />
                                    </NJFormItem>

                                    <NJFormItem label="Débit (m³/h)" style={{ marginTop: '16px' }}>
                                        <NJInput type="number" {...register('flowRate')} placeholder="Débit volumique" />
                                    </NJFormItem>

                                    <NJFormItem label="Pression (bar)" style={{ marginTop: '16px' }}>
                                        <NJInput type="number" step="0.1" {...register('pressure')} placeholder="Pression de service" />
                                    </NJFormItem>
                                </>
                            )}

                            {/* Champs spécifiques Chiller Centrifuge */}
                            {editingNodeData.entityName === 'Chiller' && editingNodeData.chillerType === 'Centrifuge' && (
                                <>
                                    {/* Section Performance */}
                                    <div style={{ marginTop: '24px', marginBottom: '12px' }}>
                                        <NJText variant="heading-small">Performance</NJText>
                                    </div>

                                    <NJFormItem label="Nominal cooling capacity [J/s] *" required style={{ marginTop: '12px' }}>
                                        <NJInput 
                                            type="number" 
                                            step="1"
                                            {...register('nominalCoolingCapacity', { required: true })} 
                                            placeholder="275" 
                                        />
                                    </NJFormItem>

                                    <NJFormItem label="Partial load minimum rate *" required style={{ marginTop: '12px' }}>
                                        <NJInput 
                                            type="number" 
                                            step="0.01"
                                            {...register('partialLoadMinRatio', { required: true })} 
                                            placeholder="0.1" 
                                        />
                                    </NJFormItem>

                                    <NJFormItem label="Nominal EER (Energy Efficiency Ratio) *" required style={{ marginTop: '12px' }}>
                                        <NJInput 
                                            type="number" 
                                            step="0.1"
                                            {...register('nominalEER', { required: true })} 
                                            placeholder="4.5" 
                                        />
                                    </NJFormItem>

                                    {/* Section Operating temperatures */}
                                    <div style={{ marginTop: '24px', marginBottom: '12px' }}>
                                        <NJText variant="heading-small">Operating temperatures</NJText>
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                                        <NJFormItem label="Evaporator minimum output temp [K] *" required>
                                            <NJInput 
                                                type="number" 
                                                step="1"
                                                {...register('evaporatorMinInletTemp', { required: true })} 
                                                placeholder="280" 
                                            />
                                        </NJFormItem>
                                        <NJFormItem label="Evaporator maximum output temp [K] *" required>
                                            <NJInput 
                                                type="number" 
                                                step="1"
                                                {...register('evaporatorMaxInletTemp', { required: true })} 
                                                placeholder="330" 
                                            />
                                        </NJFormItem>
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                                        <NJFormItem label="Condenser minimum output temp [K] *" required>
                                            <NJInput 
                                                type="number" 
                                                step="1"
                                                {...register('condenserMinInletTemp', { required: true })} 
                                                placeholder="290" 
                                            />
                                        </NJFormItem>
                                        <NJFormItem label="Condenser maximum output temp [K] *" required>
                                            <NJInput 
                                                type="number" 
                                                step="1"
                                                {...register('condenserMaxInletTemp', { required: true })} 
                                                placeholder="350" 
                                            />
                                        </NJFormItem>
                                    </div>

                                    {/* Toggle Options avancées */}
                                    <div style={{ marginTop: 'var(--nj-semantic-size-spacing-24)' }}>
                                        <NJToggle
                                            id="cold-production-advanced-options"
                                            label="Afficher les options avancées"
                                            checked={showAdvancedOptions}
                                            onChange={(_event, checked) => setShowAdvancedOptions(checked)}
                                        />
                                    </div>

                                    {/* Options avancées (cachées par défaut) */}
                                    {showAdvancedOptions && (
                                        <>
                                            <NJFormItem label="Electricity contract" style={{ marginTop: '12px' }}>
                                                <NJSelect {...register('electricityContract')}>
                                                    <option value="">Sélectionner...</option>
                                                    <option value="contrat-auber">Contrat elec Auber</option>
                                                    <option value="contrat-bercy">Contrat elec Bercy</option>
                                                    <option value="contrat-canada">Contrat elec Canada</option>
                                                    <option value="contrat-etoile">Contrat elec Etoile</option>
                                                    <option value="contrat-printemps">Contrat elec Printemps</option>
                                                    <option value="contrat-tokyo">Contrat elec Tokyo</option>
                                                    <option value="contrat-bnf">Contrat elec BNF</option>
                                                </NJSelect>
                                            </NJFormItem>

                                            <NJFormItem label="Compressor capacity" style={{ marginTop: '12px' }}>
                                                <NJSelect {...register('compressorCapacity')}>
                                                    <option value="">Sélectionner...</option>
                                                    <option value="capacity-1">Capacity 1</option>
                                                    <option value="capacity-2">Capacity 2</option>
                                                    <option value="capacity-3">Capacity 3</option>
                                                </NJSelect>
                                            </NJFormItem>

                                            <NJFormItem label="Linked exchanger" style={{ marginTop: '12px' }}>
                                                <NJInput {...register('linkedExchanger')} placeholder="Échangeur lié" />
                                            </NJFormItem>

                                            <NJFormItem label="Partial load maximum ratio" style={{ marginTop: '12px' }}>
                                                <NJInput type="number" step="0.01" {...register('partialLoadMaxRatio')} placeholder="1.0" />
                                            </NJFormItem>
                                        </>
                                    )}
                                </>
                            )}

                            {/* Champs spécifiques TAR (Tour Aéroréfrigérante) */}
                            {editingNodeData.entityName === 'CoolingTower' && (
                                <>
                                    <div style={{ marginTop: '32px', marginBottom: '16px' }}>
                                        <NJText variant="heading-small">Informations générales</NJText>
                                    </div>

                                    <NJFormItem label="Puissance électrique nominale [J/s] *" required>
                                        <NJInput 
                                            type="number" 
                                            {...register('nominalElectricalPower', { required: true })} 
                                            placeholder="65000"
                                        />
                                    </NJFormItem>

                                    <NJFormItem label="Puissance thermique nominale [J/s] *" required style={{ marginTop: '12px' }}>
                                        <NJInput 
                                            type="number" 
                                            {...register('nominalThermalPower', { required: true })} 
                                            placeholder="4e+06"
                                        />
                                    </NJFormItem>

                                    <NJFormItem label="Nombre de ventilateurs" style={{ marginTop: '12px' }}>
                                        <NJInput 
                                            type="number" 
                                            {...register('numberOfFans')} 
                                            placeholder="2"
                                        />
                                    </NJFormItem>

                                    <div style={{ marginTop: '24px', marginBottom: '16px' }}>
                                        <NJText variant="heading-small">Coefficients de puissance électrique</NJText>
                                    </div>

                                    <NJFormItem label="Puissance électrique : α *" required>
                                        <NJInput 
                                            type="number" 
                                            step="0.0001"
                                            {...register('electricPowerAlpha', { required: true })} 
                                            placeholder="-1"
                                        />
                                    </NJFormItem>

                                    <NJFormItem label="Puissance électrique : β *" required style={{ marginTop: '12px' }}>
                                        <NJInput 
                                            type="number" 
                                            step="0.0001"
                                            {...register('electricPowerBeta', { required: true })} 
                                            placeholder="3.0109"
                                        />
                                    </NJFormItem>

                                    <NJFormItem label="Puissance électrique des ventilateurs : γ *" required style={{ marginTop: '12px' }}>
                                        <NJInput 
                                            type="number" 
                                            step="0.0001"
                                            {...register('fansElectricPowerGamma', { required: true })} 
                                            placeholder="0.5551"
                                        />
                                    </NJFormItem>

                                    <NJFormItem label="Puissance électrique du ventilateur : coefficient m *" required style={{ marginTop: '12px' }}>
                                        <NJInput 
                                            type="number" 
                                            step="0.0001"
                                            {...register('fanElectricPowerCoeffM', { required: true })} 
                                            placeholder="1.3110"
                                        />
                                    </NJFormItem>

                                    <NJFormItem label="Système de refroidissements à air" style={{ marginTop: '16px' }}>
                                        <NJSelect
                                            {...register('airCoolingSystem')}
                                            placeholder="Sélectionner un système"
                                        >
                                            <option value="">Aucun système</option>
                                            <option value="refroidissement-tar-copera">Refroidissement par TAR COpera</option>
                                            <option value="refroidissement-tar-bercy">Refroidissement par TAR CBER</option>
                                            <option value="refroidissement-tar-auber">Refroidissement par TAR CAuber</option>
                                        </NJSelect>
                                    </NJFormItem>
                                </>
                            )}

                </EntityFormPanel>
            )}

            {/* Modale d'édition de groupe */}
            {editingGroupId && editingGroup && (
                <Size2MixRightSideModal
                    isOpen
                    onClose={closeGroupEditor}
                    testId="production-group-form"
                    title={String(editingGroupData.label ?? '')}
                    width="min(536px, calc(100vw - var(--nj-semantic-size-spacing-48)))"
                >
                    {getNodeIcon(editingGroupData.entityName ?? '') && (
                        <style>
                            {`
                                [data-testid="production-group-form"] > div:first-child > p {
                                    min-height: 32px;
                                    padding-left: 44px;
                                    display: flex;
                                    align-items: center;
                                    background-image: url("${getNodeIcon(editingGroupData.entityName ?? '')}");
                                    background-repeat: no-repeat;
                                    background-position: left center;
                                    background-size: 32px 32px;
                                }
                            `}
                        </style>
                    )}
                        {/* Formulaire spécifique pour groupe de pompes */}
                        {editingGroupData.entityLabel === 'Groupe de pompes' ? (
                            <form onSubmit={handleSubmitGroup(onSubmitGroup)} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', minHeight: 0 }}>
                                {/* Body scrollable */}
                                <div style={{ flex: '1 1 0', overflowY: 'auto', padding: '12px 24px 16px', minHeight: 0 }}>
                                    <NJText
                                        scale="sm"
                                        style={{ display: 'block', marginBottom: '32px', color: 'var(--nj-semantic-color-text-neutral-secondary-default)' }}
                                    >
                                        Tous les champs avec * sont obligatoires
                                    </NJText>
                                    {/* Identifiant */}
                                    <NJFormItem label="Identifiant *" required>
                                        <NJInput
                                            {...registerGroup('identifier', { required: true, maxLength: 64 })}
                                            placeholder="identifiant-technique"
                                        />
                                    </NJFormItem>
                                    
                                    {/* Nom */}
                                    <NJFormItem label="Nom *" required style={{ marginTop: '8px' }}>
                                        <NJInput 
                                            {...registerGroup('label', { required: true, maxLength: 64 })}
                                            placeholder="Nom du groupe de pompes"
                                        />
                                    </NJFormItem>

                                    {/* Contrat électrique auxiliaire */}
                                    <NJFormItem label="Auxiliary electricity contract" style={{ marginTop: '8px' }}>
                                        <NJSelect
                                            {...registerGroup('electricityContract')}
                                            placeholder="Sélectionner un contrat"
                                        >
                                            <option value="">Aucun contrat</option>
                                            <option value="contrat-elec-bercy">Contrat élec Bercy</option>
                                            <option value="contrat-elec-auber">Contrat élec Auber</option>
                                            <option value="geothermal-unit-test2">Geothermal unit test2</option>
                                        </NJSelect>
                                    </NJFormItem>
                                </div>

                                {/* Métadonnées juste au-dessus des boutons */}
                                <div style={{ 
                                    padding: '6px 24px',
                                    backgroundColor: 'var(--nj-semantic-color-surface-neutral-subtle)', 
                                    borderTop: '1px solid var(--nj-core-color-grey-200)',
                                    fontSize: '10px',
                                    lineHeight: '1.2',
                                    color: 'var(--nj-semantic-color-text-neutral-secondary-default)',
                                    flexShrink: 0
                                }}>
                                    Créé par Erika Dal Monte le 19-06-2025 à 15:41:51, dernière modification: 09-02-2025 à 11:37:53
                                </div>

                                {/* Boutons fixes en bas */}
                                <div style={{ 
                                    padding: '10px 24px', 
                                    borderTop: '1px solid var(--nj-core-color-grey-200)', 
                                    display: 'flex', 
                                    gap: '12px', 
                                    justifyContent: 'flex-end',
                                    backgroundColor: 'white',
                                    flexShrink: 0
                                }}>
                                    <NJButton
                                        type="button"
                                        variant="secondary"
                                        onClick={closeGroupEditor}
                                        label="Annuler"
                                    />
                                    <NJButton
                                        type="submit"
                                        variant="primary"
                                        disabled={!isGroupDirty || !isGroupValid}
                                        label={editingGroupData.isNew ? 'Créer' : 'Valider'}
                                    />
                                </div>
                            </form>
                        ) : editingGroupData.entityName === 'WaterCooling' ? (
                            /* Formulaire pour système de refroidissement */
                            <form onSubmit={handleSubmitGroup(onSubmitGroup)} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', minHeight: 0 }}>
                                {/* Body scrollable */}
                                <div style={{ flex: '1 1 0', overflowY: 'auto', padding: '12px 24px 16px', minHeight: 0 }}>
                                    <NJText
                                        scale="sm"
                                        style={{ display: 'block', marginBottom: '32px', color: 'var(--nj-semantic-color-text-neutral-secondary-default)' }}
                                    >
                                        Tous les champs avec * sont obligatoires
                                    </NJText>
                                    {/* Section: Informations générales */}
                                    <div style={{ marginBottom: '20px' }}>
                                        <NJText variant="heading-small">Informations générales</NJText>
                                    </div>

                                    {/* Nom */}
                                    <NJFormItem label="Nom *" required>
                                        <NJInput 
                                            {...registerGroup('label', { required: true, maxLength: 64 })}
                                            placeholder="Nom du système de refroidissement"
                                        />
                                    </NJFormItem>

                                    {/* Lignes de production liées */}
                                    <NJFormItem label="Lignes de production liées" style={{ marginTop: '8px' }}>
                                        <select 
                                            {...registerGroup('linkedProductionLines')}
                                            multiple 
                                            style={{
                                                width: '100%',
                                                minHeight: '120px',
                                                padding: '8px',
                                                border: '1px solid var(--nj-core-color-grey-300)',
                                                borderRadius: '4px',
                                                fontFamily: 'var(--nj-core-font-family-sans)',
                                                fontSize: '14px',
                                            }}
                                        >
                                            {nodes
                                                .filter(n => n.type === 'group' && n.data.entityLabel === 'Ligne de production')
                                                .map(line => (
                                                    <option key={line.id} value={line.id}>
                                                        {line.data.label}
                                                    </option>
                                                ))
                                            }
                                        </select>
                                    </NJFormItem>

                                    {/* Identifiant */}
                                    <NJFormItem label="Identifiant *" required style={{ marginTop: '8px' }}>
                                        <NJInput
                                            {...registerGroup('identifier', { required: true, maxLength: 64 })}
                                            placeholder="identifiant-technique"
                                        />
                                    </NJFormItem>

                                    {/* T Condenseur Entrée min [K] */}
                                    <NJFormItem label="T Condenseur Entrée min [K] *" required style={{ marginTop: '8px' }}>
                                        <NJInput 
                                            type="number" 
                                            step="0.01"
                                            {...registerGroup('condenserInletTempMin', { required: true })} 
                                            placeholder="288.15"
                                        />
                                    </NJFormItem>

                                    {/* T Condenseur Sortie max [K] */}
                                    <NJFormItem label="T Condenseur Sortie max [K] *" required style={{ marginTop: '8px' }}>
                                        <NJInput 
                                            type="number" 
                                            step="0.01"
                                            {...registerGroup('condenserOutletTempMax', { required: true })} 
                                            placeholder="303.15"
                                        />
                                    </NJFormItem>

                                    {/* Contrat d'électricité */}
                                    <NJFormItem label="Contrat d'électricité" style={{ marginTop: '8px' }}>
                                        <NJSelect {...registerGroup('electricityContract')}>
                                            <option value="">Sélectionner un contrat</option>
                                            <option value="contrat-elec-bercy">Contrat élec Bercy</option>
                                            <option value="contrat-elec-auber">Contrat élec Auber</option>
                                        </NJSelect>
                                    </NJFormItem>

                                    {/* Contrat d'eau */}
                                    <NJFormItem label="Contrat d'eau" style={{ marginTop: '8px' }}>
                                        <NJSelect {...registerGroup('waterContract')}>
                                            <option value="">Sélectionner un contrat</option>
                                            <option value="TAR_Water_Contract_Opera">TAR_Water_Contract_Opera</option>
                                        </NJSelect>
                                    </NJFormItem>

                                    {/* Tour aéroréfrigérante (TARs dynamiques) */}
                                    <NJFormItem label="Tour aéroréfrigérante *" required style={{ marginTop: '8px' }}>
                                        <select 
                                            {...registerGroup('linkedCoolingTowers', { required: true })}
                                            multiple 
                                            size={Math.min(nodes.filter(n => n.parentId === editingGroupId && n.data.entityName === 'CoolingTower').length + 1, 7)}
                                            style={{
                                                width: '100%',
                                                minHeight: '100px',
                                                padding: '8px',
                                                border: '1px solid var(--nj-core-color-grey-300)',
                                                borderRadius: '4px',
                                                fontFamily: 'var(--nj-core-font-family-sans)',
                                                fontSize: '14px',
                                            }}
                                        >
                                            {(() => {
                                                const availableTARs = nodes.filter(n => n.parentId === editingGroupId && n.data.entityName === 'CoolingTower');
                                                if (availableTARs.length === 0) {
                                                    return <option disabled>Aucune TAR trouvée dans ce groupe</option>;
                                                }
                                                return availableTARs.map(tar => (
                                                    <option key={tar.id} value={tar.id}>
                                                        {tar.data.label}
                                                    </option>
                                                ));
                                            })()}
                                        </select>
                                        <div style={{ fontSize: '11px', color: 'var(--nj-semantic-color-text-neutral-secondary-default)', marginTop: '4px' }}>
                                            {(() => {
                                                const count = nodes.filter(n => n.parentId === editingGroupId && n.data.entityName === 'CoolingTower').length;
                                                return `${count} TAR${count > 1 ? 's' : ''} disponible${count > 1 ? 's' : ''} dans le groupe de refroidissement`;
                                            })()}
                                        </div>
                                    </NJFormItem>

                                    {/* Coefficients T Condenseur Entrée */}
                                    <div style={{ marginTop: '20px', marginBottom: '12px' }}>
                                        <NJText variant="heading-small">Coefficients de température</NJText>
                                    </div>

                                    {/* T Condenseur Entrée α */}
                                    <NJFormItem label="T Condenseur Entrée α" style={{ marginTop: '8px' }}>
                                        <NJInput 
                                            type="number" 
                                            step="0.0001"
                                            {...registerGroup('condenserInletAlpha')} 
                                            placeholder="-9.3925"
                                        />
                                        <div style={{ fontSize: '11px', color: 'var(--nj-semantic-color-text-neutral-secondary-default)', marginTop: '4px' }}>
                                            T Condenseur Entrée = α · T_hum_ide + β · γ · T_s_che + η · T²_hum_ide
                                        </div>
                                    </NJFormItem>

                                    {/* T Condenseur Entrée β [K] */}
                                    <NJFormItem label="T Condenseur Entrée β [K]" style={{ marginTop: '8px' }}>
                                        <NJInput 
                                            type="number" 
                                            step="0.1"
                                            {...registerGroup('condenserInletBeta')} 
                                            placeholder="1528.6"
                                        />
                                    </NJFormItem>

                                    {/* T Condenseur Entrée γ */}
                                    <NJFormItem label="T Condenseur Entrée γ" style={{ marginTop: '8px' }}>
                                        <NJInput 
                                            type="number" 
                                            step="0.0001"
                                            {...registerGroup('condenserInletGamma')} 
                                            placeholder="0.2161"
                                        />
                                    </NJFormItem>

                                    {/* T Condenseur Entrée η [1/K] */}
                                    <NJFormItem label="T Condenseur Entrée η [1/K]" style={{ marginTop: '8px' }}>
                                        <NJInput 
                                            type="number" 
                                            step="0.0001"
                                            {...registerGroup('condenserInletEta')} 
                                            placeholder="0.0169"
                                        />
                                    </NJFormItem>

                                    {/* Débit d'eau */}
                                    <div style={{ marginTop: '20px', marginBottom: '12px' }}>
                                        <NJText variant="heading-small">Débits et performances</NJText>
                                    </div>

                                    {/* Débit d'eau α [m³/s/W] */}
                                    <NJFormItem label="Débit d'eau : α [m³/s/W] *" required style={{ marginTop: '8px' }}>
                                        <NJInput 
                                            type="number" 
                                            step="0.0001"
                                            {...registerGroup('waterFlowAlpha', { required: true })} 
                                            placeholder="4.0272"
                                        />
                                        <div style={{ fontSize: '11px', color: 'var(--nj-semantic-color-text-neutral-secondary-default)', marginTop: '4px' }}>
                                            Débit de l'eau (m3/s) = [alpha] * Qref (W) + beta
                                        </div>
                                    </NJFormItem>

                                    {/* Débit d'eau β [m/s] */}
                                    <NJFormItem label="Débit d'eau β [m/s]" style={{ marginTop: '8px' }}>
                                        <NJInput 
                                            type="number" 
                                            step="0.001"
                                            {...registerGroup('waterFlowBeta')} 
                                            placeholder="0"
                                        />
                                        <div style={{ fontSize: '11px', color: 'var(--nj-semantic-color-text-neutral-secondary-default)', marginTop: '4px' }}>
                                            Débit de l'eau (m3/s) = alpha * Qref (W) + [beta]
                                        </div>
                                    </NJFormItem>

                                    {/* Rendement adoucisseur/osmoseur */}
                                    <NJFormItem label="Rendement adoucisseur/osmoseur *" required style={{ marginTop: '8px' }}>
                                        <NJInput 
                                            type="number" 
                                            step="0.01"
                                            {...registerGroup('softenerEfficiency', { required: true })} 
                                            placeholder="0.05"
                                        />
                                    </NJFormItem>

                                    {/* Hauteur statique circuit TAR [Pa] */}
                                    <NJFormItem label="Hauteur statique circuit TAR [Pa]" style={{ marginTop: '8px' }}>
                                        <NJInput 
                                            type="number" 
                                            step="1"
                                            {...registerGroup('tarStaticHeight')} 
                                            placeholder="0"
                                        />
                                        <div style={{ fontSize: '11px', color: 'var(--nj-semantic-color-text-neutral-secondary-default)', marginTop: '4px' }}>
                                            Utilisé dans l'estimation du point de fonctionnement des pompes condenseur (Option) (terme constant)
                                        </div>
                                    </NJFormItem>

                                    {/* Perte de charge TAR (buses) [Pa] */}
                                    <NJFormItem label="Perte de charge TAR (buses) [Pa]" style={{ marginTop: '8px' }}>
                                        <NJInput 
                                            type="number" 
                                            step="1"
                                            {...registerGroup('tarPressureLoss')} 
                                            placeholder="0"
                                        />
                                        <div style={{ fontSize: '11px', color: 'var(--nj-semantic-color-text-neutral-secondary-default)', marginTop: '4px' }}>
                                            Utilisé dans l'estimation du point de fonctionnement des pompes condenseur (Option) (terme variable fonction du débit du circuit)
                                        </div>
                                    </NJFormItem>

                                    {/* Débit nominal global TAR [m³/s] */}
                                    <NJFormItem label="Débit nominal global TAR [m³/s]" style={{ marginTop: '8px' }}>
                                        <NJInput 
                                            type="number" 
                                            step="0.001"
                                            {...registerGroup('tarNominalFlow')} 
                                            placeholder="0"
                                        />
                                        <div style={{ fontSize: '11px', color: 'var(--nj-semantic-color-text-neutral-secondary-default)', marginTop: '4px' }}>
                                            Utilisé comme valeur de référence pour la perte de charge nominale du circuit TAR pour l'estimation du point de fonctionnement des pompes condenseur (Option)
                                        </div>
                                    </NJFormItem>
                                </div>

                                {/* Métadonnées juste au-dessus des boutons */}
                                <div style={{ 
                                    padding: '6px 24px',
                                    backgroundColor: 'var(--nj-semantic-color-surface-neutral-subtle)', 
                                    borderTop: '1px solid var(--nj-core-color-grey-200)',
                                    fontSize: '10px',
                                    lineHeight: '1.2',
                                    color: 'var(--nj-semantic-color-text-neutral-secondary-default)',
                                    flexShrink: 0
                                }}>
                                    Créé par Erika Dal Monte le 19-06-2025 à 15:41:51, dernière modification: 09-02-2025 à 11:37:53
                                </div>

                                {/* Boutons fixes en bas */}
                                <div style={{ 
                                    padding: '10px 24px', 
                                    borderTop: '1px solid var(--nj-core-color-grey-200)', 
                                    display: 'flex', 
                                    gap: '12px', 
                                    justifyContent: 'flex-end',
                                    backgroundColor: 'white',
                                    flexShrink: 0
                                }}>
                                    <NJButton
                                        type="button"
                                        variant="secondary"
                                        onClick={closeGroupEditor}
                                        label="Annuler"
                                    />
                                    <NJButton
                                        type="submit"
                                        variant="primary"
                                        disabled={!isGroupDirty || !isGroupValid}
                                        label={editingGroupData.isNew ? 'Créer' : 'Valider'}
                                    />
                                </div>
                            </form>
                        ) : (
                            /* Formulaire pour ligne de production (existant) */
                            <form onSubmit={handleSubmitGroup(onSubmitGroup)} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                                {/* Body scrollable */}
                                <div style={{ flex: 1, overflowY: 'auto', padding: '24px', minHeight: 0 }}>
                                    <NJText
                                        scale="sm"
                                        style={{ display: 'block', marginBottom: '32px', color: 'var(--nj-semantic-color-text-neutral-secondary-default)' }}
                                    >
                                        Tous les champs avec * sont obligatoires
                                    </NJText>
                                    {/* Identifiant */}
                                    <NJFormItem label="Identifiant *" required>
                                        <NJInput
                                            {...registerGroup('identifier', { required: true, maxLength: 64 })}
                                            placeholder="identifiant-technique"
                                        />
                                    </NJFormItem>
                                    
                                    {/* Nom */}
                                    <NJFormItem label="Nom *" required style={{ marginTop: '16px' }}>
                                        <NJInput 
                                            {...registerGroup('label', { required: true, maxLength: 64 })} 
                                            placeholder="Nom de la ligne de production"
                                        />
                                    </NJFormItem>

                                    {/* Type (read-only) */}
                                    <NJFormItem label="Type" style={{ marginTop: '16px' }}>
                                        <NJInput value={editingGroupData.entityLabel} disabled style={{ backgroundColor: 'var(--nj-semantic-color-surface-disabled)' }} />
                                    </NJFormItem>

                                    {/* Configuration de condenseur */}
                                    <div style={{ marginTop: '32px', marginBottom: '16px' }}>
                                        <NJText variant="heading-small">Configuration des groupes froids</NJText>
                                    </div>

                                    <NJFormItem label="Type de configuration des condenseurs *" required style={{ marginTop: '16px' }}>
                                        <NJSelect
                                            value={condenserType}
                                            onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                                                setCondenserType(e.target.value as CondenserCoolingType)
                                            }
                                        >
                                            <option value="parallel">Condenseurs en parallèle</option>
                                            <option value="series">Condenseurs en série</option>
                                        </NJSelect>
                                    </NJFormItem>

                                    <div style={{ 
                                        marginTop: '16px', 
                                        padding: '12px', 
                                        backgroundColor: 'var(--nj-semantic-color-surface-info-subtle)', 
                                        borderRadius: 'var(--nj-semantic-size-border-radius-none)',
                                        border: '1px solid var(--nj-semantic-color-border-info-default)'
                                    }}>
                                        <NJText variant="body-small">
                                            <strong>Condenseurs en parallèle :</strong> Les 2 groupes froids fonctionnent en parallèle, sans connexion entre eux.<br/><br/>
                                            <strong>Condenseurs en série :</strong> Les 2 groupes froids sont reliés en série par une ligne bleue.
                                        </NJText>
                                    </div>
                                </div>

                                <div style={{
                                    padding: '6px 24px',
                                    borderTop: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
                                    fontSize: '10px',
                                    lineHeight: '1.2',
                                    color: 'var(--nj-semantic-color-text-neutral-secondary-default)',
                                    fontStyle: 'italic',
                                    flexShrink: 0,
                                }}>
                                    Créé par nemo - e2e-analyst-1 le 23-10-2026 à 10:50:48, dernière modification 23-10-2026 à 10:50:48
                                </div>

                                {/* Boutons fixes en bas */}
                                <div style={{ 
                                    padding: '16px 24px', 
                                    borderTop: '1px solid var(--nj-core-color-grey-200)', 
                                    display: 'flex', 
                                    gap: '12px', 
                                    justifyContent: 'flex-end',
                                    backgroundColor: 'white',
                                    flexShrink: 0
                                }}>
                                    <NJButton
                                        type="button"
                                        variant="secondary"
                                        onClick={closeGroupEditor}
                                        label="Annuler"
                                    />
                                    <NJButton
                                        type="submit"
                                        variant="primary"
                                        disabled={!isGroupDirty || !isGroupValid}
                                        label={editingGroupData.isNew ? 'Créer' : 'Valider'}
                                    />
                                </div>
                            </form>
                        )}
                </Size2MixRightSideModal>
            )}

            {/* Modale de suppression */}
            {deletingNodeId !== null && (
                <NJModal isOpen={true}>
                    <NJModalContentInformation
                        id="deleteNodeModal"
                        icon="info_outline"
                        iconVariant="red"
                        centeredFooter={true}
                        title="Confirmation de suppression"
                        onClose={() => setDeletingNodeId(null)}
                    >
                        <div style={{ marginBottom: 24 }}>
                            <NJText variant="body-default">
                                Êtes-vous sûr de vouloir supprimer {deletingNodeData.entityLabel?.toLowerCase()} <strong>{deletingNodeData.label}</strong> ?
                            </NJText>
                        </div>
                        <NJButton
                            label="Annuler"
                            variant="secondary"
                            className="cold-delete-modal__cancel"
                            onClick={() => setDeletingNodeId(null)}
                        />
                        <NJButton
                            label="Je supprime"
                            variant="primary"
                            className="cold-delete-modal__confirm"
                            onClick={handleDeleteNode}
                        />
                    </NJModalContentInformation>
                </NJModal>
            )}
        </div>
    );
}

// ── Layout automatique ──────────────────────────────────────────────────

function applyAutoLayout(nodes: Node<IBaseData>[], edges: Edge[]): Node<IBaseData>[] {
    // Créer une map des nœuds par ID pour accès rapide
    // Calculer les niveaux (layers) pour chaque nœud basé sur les edges
    const levels = new Map<string, number>();
    const visited = new Set<string>();
    
    // Trouver les nœuds sources (sans edges entrants ou types spécifiques)
    const sourceNodes = nodes.filter(n => {
        const hasIncomingEdge = edges.some(e => e.target === n.id);
        const isSource = n.data.entityName === 'Return' || 
                        n.data.entityName === 'WaterCooling' || 
                        n.data.entityName === 'PowerContract';
        return !hasIncomingEdge || isSource;
    });
    
    // BFS pour assigner les niveaux
    const queue: Array<{ nodeId: string; level: number }> = sourceNodes.map(n => ({ nodeId: n.id, level: 0 }));
    
    while (queue.length > 0) {
        const { nodeId, level } = queue.shift()!;
        if (visited.has(nodeId)) continue;
        visited.add(nodeId);
        levels.set(nodeId, level);
        
        // Trouver les nœuds enfants
        const outgoingEdges = edges.filter(e => e.source === nodeId);
        for (const edge of outgoingEdges) {
            if (!visited.has(edge.target)) {
                queue.push({ nodeId: edge.target, level: level + 1 });
            }
        }
    }
    
    // Grouper les nœuds par niveau
    const nodesByLevel = new Map<number, Node<IBaseData>[]>();
    for (const node of nodes) {
        if (node.parentId) continue; // Skip enfants de groupes
        const level = levels.get(node.id) ?? 0;
        if (!nodesByLevel.has(level)) {
            nodesByLevel.set(level, []);
        }
        nodesByLevel.get(level)!.push(node);
    }
    
    // Paramètres de layout
    const LEVEL_SPACING = 350; // Espacement horizontal entre niveaux
    const NODE_SPACING = 30; // Espacement vertical entre nœuds
    const START_X = 80;
    const START_Y = 80;
    
    // Positionner les nœuds
    const layoutedNodes = nodes.map(node => {
        if (node.parentId) {
            // Garder les positions relatives pour les enfants de groupes
            return node;
        }
        
        const level = levels.get(node.id) ?? 0;
        const nodesInLevel = nodesByLevel.get(level) ?? [];
        const indexInLevel = nodesInLevel.indexOf(node);
        
        const x = START_X + (level * LEVEL_SPACING);
        const y = START_Y + (indexInLevel * (200 + NODE_SPACING));
        
        return {
            ...node,
            position: { x, y },
        };
    });
    
    return layoutedNodes;
}

// ── Main Component ──────────────────────────────────────────────────────

export default function CoolingSystem2() {
    const nav = useSize2MixRefNavigation();
    const [searchParams, setSearchParams] = useSearchParams();
    const viewerParam = searchParams.get('as');
    const viewerId: ViewerId = isViewerId(viewerParam) ? viewerParam : 'owner';
    const viewer = VIEWERS[viewerId];
    const [activeTab, setActiveTab] = useState<TabKey>('editor');
    const [selectedPlantId, setSelectedPlantId] = useState<PlantId>('cber');
    const [resetVersion, setResetVersion] = useState(0);
    
    // État pour les configurations de groupes (série vs parallèle)
    const [groupConfigs, setGroupConfigs] = useState<GroupConfigs>(() => {
        const storageKey = `cooling-system-2-group-configs-${selectedPlantId}`;
        const saved = localStorage.getItem(storageKey);
        if (saved) {
            try {
                return JSON.parse(saved);
            } catch (e) {
                console.warn('Failed to load group configs', e);
            }
        }
        return {};
    });

    // Sauvegarder les configurations de groupes dans localStorage
    useEffect(() => {
        const storageKey = `cooling-system-2-group-configs-${selectedPlantId}`;
        localStorage.setItem(storageKey, JSON.stringify(groupConfigs));
    }, [groupConfigs, selectedPlantId]);

    const handleGroupConfigChange = useCallback((groupId: string, config: CondenserCoolingType) => {
        setGroupConfigs((prev) => ({ ...prev, [groupId]: config }));
    }, []);

    const setViewer = (nextViewer: ViewerId) => {
        const next = new URLSearchParams(searchParams);
        next.set('as', nextViewer);
        setSearchParams(next, { replace: true });
    };

    const resetPrototype = () => {
        VISIBLE_PLANTS.forEach((plant) => {
            localStorage.removeItem(`cooling-system-2-${plant.id}`);
            localStorage.removeItem(`cooling-system-2-group-configs-${plant.id}`);
        });
        setGroupConfigs({});
        setResetVersion((version) => version + 1);
        NJToastService('Schéma réinitialisé', { iconName: 'restart_alt', dismissAfter: 2500 });
    };

    // Générer le graphe en fonction de la centrale sélectionnée avec layout automatique
    const { nodes: initialNodes, edges: initialEdges } = useMemo(
        () => {
            const graph = buildGraphForPlant(selectedPlantId, groupConfigs);
            let layoutedNodes = applyAutoLayout(graph.nodes, graph.edges);
            
            // Restaurer les positions ET les data sauvegardées depuis localStorage
            const storageKey = `cooling-system-2-${selectedPlantId}`;
            const saved = localStorage.getItem(storageKey);
            if (saved) {
                try {
                    const savedNodes = JSON.parse(saved) as PersistedFlowNode[];
                    const initialNodeIds = new Set(layoutedNodes.map((node) => node.id));
                    layoutedNodes = layoutedNodes.map((node) => {
                        const savedNode = savedNodes.find((p) => p.id === node.id);
                        if (savedNode) {
                            const isBercyCoolingSystem = node.id === 'cooling-bercy';
                            const savedLabel =
                                savedNode.data.label === 'Système de refroidissement à eau BERCY'
                                    ? 'Système de refroidissement à air BERCY'
                                    : savedNode.data.label;
                            return { 
                                ...node, 
                                position: savedNode.position,
                                parentId: savedNode.parentId ?? node.parentId,
                                width: savedNode.width ?? node.width,
                                height: savedNode.height ?? node.height,
                                data: {
                                    ...node.data,
                                    ...savedNode.data,
                                    ...(isBercyCoolingSystem
                                        ? {
                                            entityLabel: 'Système de refroidissement à air',
                                            label: savedLabel,
                                            coolingSystemType: 'air',
                                        }
                                        : {}),
                                },
                            };
                        }
                        return node;
                    });
                    const createdNodes: FlowNode[] = savedNodes
                        .filter((savedNode) => !initialNodeIds.has(savedNode.id))
                        .map((savedNode) => ({
                            id: savedNode.id,
                            type: savedNode.type ?? 'asset',
                            position: savedNode.position,
                            parentId: savedNode.parentId,
                            width: savedNode.width,
                            height: savedNode.height,
                            data: savedNode.data,
                        }));
                    layoutedNodes = [...layoutedNodes, ...createdNodes];
                } catch (e) {
                    console.warn('Failed to load saved nodes', e);
                }
            }
            
            return {
                nodes: formatProductionLayout(layoutedNodes),
                edges: graph.edges,
            };
        },
        [groupConfigs, resetVersion, selectedPlantId],
    );

    return (
        <PrototypeStudioLayout title={meta.title} commentScope={selectedPlantId}>
            <NetworkShell
                network={COLD_NETWORK}
                activePage="production"
                onNavigate={nav.onNetworkNavigate}
                onPrimaryNavigate={nav.onPrimaryNavigate}
            >
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <Size2MixTabs<TabKey>
                        tabs={COLD_PRODUCTION_TABS}
                        activeId={activeTab}
                        onChange={setActiveTab}
                        ariaLabel="Production eau froide"
                    />

                    <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
                        {activeTab === 'editor' && (
                            <ProductionEditorProvider
                                key={`${selectedPlantId}-${resetVersion}-${JSON.stringify(groupConfigs)}`}
                                initialNodes={initialNodes}
                                initialEdges={initialEdges}
                            >
                                <div style={{ flex: 1, position: 'relative' }}>
                                    <CoolingSystem2Canvas
                                        selectedPlantId={selectedPlantId}
                                        onPlantChange={setSelectedPlantId}
                                        groupConfigs={groupConfigs}
                                        onGroupConfigChange={handleGroupConfigChange}
                                        viewerId={viewerId}
                                        canWrite={viewer.canWrite}
                                    />
                                </div>
                            </ProductionEditorProvider>
                        )}

                        {activeTab === 'contracts' && (
                            <EnergyContractsTab canWrite={viewer.canWrite} />
                        )}

                        {activeTab === 'synthesis' && (
                            <div style={{ flex: 1, padding: '48px 24px', textAlign: 'center' }}>
                                <NJText variant="heading-medium">Synthèse</NJText>
                                <NJText
                                    variant="body-default"
                                    style={{ marginTop: '8px', color: 'var(--nj-core-color-grey-600)' }}
                                >
                                    Vue synthétique du réseau d'eau froide.
                                </NJText>
                            </div>
                        )}
                    </div>
                </div>
            </NetworkShell>
            <ViewerSwitcher viewer={viewerId} onChange={setViewer} onReset={resetPrototype} />
            <NJToastContainer />
        </PrototypeStudioLayout>
    );
}
